import { Component, OnInit, OnDestroy, ViewChild, TemplateRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { timer as observableTimer, Observable } from 'rxjs';

import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';

import { AuthService } from '../../services/common/auth.service';
import { BillingService } from '../../services/accounting/billing.service';
import { NotyService } from '../../services/common/noty.service';
import { PhoneCallBillingService, PhoneCallBillingContext } from '../../services/phone-calls/phone-call-billing.service';

import { Billing } from '../../models/billing';

@Component({
  selector: 'app-phone-call-billing-modal',
  templateUrl: './phone-call-billing-modal.component.html',
  styleUrls: ['./phone-call-billing-modal.component.scss'],
})
export class PhoneCallBillingModalComponent implements OnInit, OnDestroy {
  @ViewChild('phoneCallBillingModal', { static: true }) phoneCallBillingModal: TemplateRef<any>;

  modalRef: NgbModalRef | null = null;
  modalOpenSubscription: Subscription;

  // Context
  phoneNumber: string = '';
  contactName: string = '';
  matterId: number | null = null;
  matterLabel: string = '';

  // Matter finder
  suggestedMatters: Array<any> = [];
  loadingMatters: boolean = false;
  matterSelected: boolean = false;

  // Billing
  billing: Billing = new Billing();
  activityRate: any = {};
  formOptions: any = { activities: [], users: [] };
  formOptionsLoaded: boolean = false;
  saving: boolean = false;

  // Timer
  timerSubscription: Subscription | null = null;
  timerStartedAt: number = 0;
  lapTime: number = 0;
  timerDisplay: string = '00:00';
  quantity: string = '0.20';
  totalCharge: string = '0.00';

  // History
  historyMessage: string = '';

  constructor(
    private authService: AuthService,
    private billingService: BillingService,
    private modalService: NgbModal,
    private notyService: NotyService,
    private phoneCallBillingService: PhoneCallBillingService
  ) {}

  ngOnInit() {
    this.modalOpenSubscription = this.phoneCallBillingService.modalOpen$.subscribe((context: PhoneCallBillingContext) => {
      this.openModal(context);
    });
  }

  ngOnDestroy() {
    if (this.modalOpenSubscription) this.modalOpenSubscription.unsubscribe();
    this.stopTimer();
  }

  openModal(context: PhoneCallBillingContext) {
    this.resetState();
    this.phoneNumber = context.phoneNumber;
    this.contactName = context.contactName || context.phoneNumber;
    this.billing.description = `Phone call to ${this.contactName}`;

    if (context.matterId) {
      this.matterId = context.matterId;
      this.matterSelected = true;
      this.loadFormOptions();
    } else {
      this.loadSuggestedMatters();
    }

    this.startTimer();

    this.modalRef = this.modalService.open(this.phoneCallBillingModal, {
      size: 'lg',
      backdrop: 'static',
      keyboard: false,
    });

    this.modalRef.result.then(
      (result) => {
        if (result === 'save') {
          this.saveBilling();
        } else {
          this.stopTimer();
        }
      },
      () => {
        // Dismiss (X button or backdrop) — just stop timer, don't auto-save
        this.stopTimer();
      }
    );
  }

  // Matter selection
  loadSuggestedMatters() {
    this.loadingMatters = true;
    this.phoneCallBillingService.findMattersForPhoneNumber(this.phoneNumber).then((response: any) => {
      this.suggestedMatters = response.matters || [];
      this.loadingMatters = false;
    }).catch(() => {
      this.loadingMatters = false;
    });
  }

  selectMatter(matter: any) {
    this.matterId = matter.id;
    this.matterLabel = matter.label;
    this.matterSelected = true;
    this.billing.matter_id = matter.id;
    this.loadFormOptions();
  }

  // Form options
  loadFormOptions() {
    if (!this.matterId) return;
    this.billing.matter_id = this.matterId;
    this.billingService.getFormOptions(this.matterId).then((response: any) => {
      this.formOptions = response;
      this.billing.created_by = response.current_user_id;
      this.billing.activity_rate_id = response.activity_rate.id;
      this.billing.activity_id = response.default_activity_id;
      this.billing.user_id = response.current_user_id;
      this.billing.is_billable = true;
      this.billing.is_hourly = true;
      this.activityRate = response.activity_rate;

      // Fetch the proper rate based on user and matter role (same as billing-record-time)
      this.billingService.getRate(
        this.billing.activity_id,
        this.billing.user_id,
        this.matterId!,
        0
      ).then((rateResponse: any) => {
        if (rateResponse && rateResponse[0]) {
          this.activityRate = rateResponse[0];
          this.billing.rate = this.activityRate.rate;
          this.billing.user_id = this.activityRate.user_id || this.billing.user_id;
          if (!this.activityRate.allow_time) {
            this.billing.quantity = this.activityRate.quantity;
          }
        } else {
          this.billing.rate = response.activity_rate.rate;
        }
        this.formOptionsLoaded = true;
        this.updateFromTimer();
      }).catch(() => {
        this.billing.rate = response.activity_rate.rate;
        this.formOptionsLoaded = true;
        this.updateFromTimer();
      });
    });
  }

  // Timer
  startTimer() {
    this.timerStartedAt = new Date().getTime();
    this.timerSubscription = observableTimer(1000, 1000).subscribe(() => {
      this.updateTimerDisplay();
      this.updateFromTimer();
    });
  }

  stopTimer() {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
      this.timerSubscription = null;
    }
    this.lapTime = this.timerStartedAt > 0 ? new Date().getTime() - this.timerStartedAt : 0;
  }

  updateTimerDisplay() {
    const elapsed = new Date().getTime() - this.timerStartedAt;
    const totalSeconds = Math.floor(elapsed / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    this.timerDisplay = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  updateFromTimer() {
    if (!this.formOptionsLoaded) return;
    const elapsed = new Date().getTime() - this.timerStartedAt;
    const hours = elapsed / 3600000;
    this.billing.quantity = hours < 0.2 ? 0.2 : +hours.toFixed(2);
    this.billing.charge = this.billing.rate * this.billing.quantity;
    this.quantity = this.billing.quantity.toFixed(2);
    this.totalCharge = this.billing.charge.toFixed(2);
  }

  manualTimeRateChange() {
    this.billing.quantity = Number(this.quantity);
    this.billing.charge = this.billing.quantity * this.billing.rate;
    this.totalCharge = this.billing.charge.toFixed(2);
  }

  // Save
  saveBilling() {
    if (!this.matterId || !this.formOptionsLoaded || this.saving) return;
    this.saving = true;
    this.stopTimer();
    this.updateFromTimer();

    this.billing.quantity = +this.quantity;
    this.billing.history_message = this.historyMessage;
    this.billing.title = `Phone call to ${this.contactName}`;

    this.billingService.create(this.billing, null as any, this.activityRate)
      .then(() => {
        this.saving = false;
        this.notyService.notify('Phone call billing saved', 'success');
        if (this.modalRef) {
          try { this.modalRef.close('saved'); } catch (e) {}
          this.modalRef = null;
        }
      })
      .catch((err: any) => {
        this.saving = false;
        const errorMsg = err?.error?.error || err?.error?.text || 'Error saving billing';
        this.notyService.notify(errorMsg, 'error');
      });
  }

  discard() {
    this.stopTimer();
    if (this.modalRef) {
      this.modalRef.close('discard');
      this.modalRef = null;
    }
  }

  resetState() {
    this.phoneNumber = '';
    this.contactName = '';
    this.matterId = null;
    this.matterLabel = '';
    this.suggestedMatters = [];
    this.matterSelected = false;
    this.formOptionsLoaded = false;
    this.saving = false;
    this.billing = new Billing();
    this.activityRate = {};
    this.formOptions = { activities: [], users: [] };
    this.timerDisplay = '00:00';
    this.quantity = '0.20';
    this.totalCharge = '0.00';
    this.historyMessage = '';
    this.timerStartedAt = 0;
    this.lapTime = 0;
    this.stopTimer();
  }
}
